import { setACCGAccountingPeriod } from '../../store/slices/accounting-and-finance/accg-accounting-period.slice';
import {
  AccountingPeriodList,
  CreateAccountingPeriodPayload,
  UpdateAccountingPeriodPayload,
  IAccountingPeriodResponse,
} from '../../modules/accounting-and-finance/interface/accounting-period/accounting-period-interface';
import { handleError } from '../../utils/api-error-handling';
import { encryptPayload } from '../../utils/security';
import { api } from '@services/api';

export const accountingPeriodApi = api
  .enhanceEndpoints({ addTagTypes: ['Accounting Period'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getAccountingPeriod: builder.query({
        query: ({ accountingPeriodID }) => {
          return `accounting-periods${
            accountingPeriodID ? `/${accountingPeriodID}` : ''
          }`;
        },
        providesTags: ['Accounting Period'],
        transformResponse: async (response: AccountingPeriodList) => {
          return response?.data;
        },
        async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
          try {
            const { data: response } = await queryFulfilled;
            if (!response) return;
            // Set state or dispatch an action
            dispatch(setACCGAccountingPeriod(response));
          } catch (error) {
            console.error(error);
          }
        },
        transformErrorResponse: async (baseQueryReturnValue) => {
          return handleError(baseQueryReturnValue);
        },
        forceRefetch() {
          return true;
        },
      }),
      createAccountingPeriod: builder.mutation({
        query: (payload: CreateAccountingPeriodPayload) => ({
          url: 'accounting-periods',
          method: 'POST',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(
              accountingPeriodApi.util.invalidateTags(['Accounting Period']),
            );
          }
        },
        transformResponse: async (response: IAccountingPeriodResponse) => {
          return response?.data;
        },
        transformErrorResponse: async (baseQueryReturnValue) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      updateAccountingPeriod: builder.mutation({
        query: (payload: UpdateAccountingPeriodPayload) => ({
          url: 'accounting-periods',
          method: 'PUT',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(
              accountingPeriodApi.util.invalidateTags(['Accounting Period']),
            );
          }
        },
        transformResponse: async (response: IAccountingPeriodResponse) => {
          return response?.data;
        },
        transformErrorResponse: async (baseQueryReturnValue) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      deleteAccountingPeriod: builder.mutation({
        query: (payload) => ({
          url: `accounting-periods/${payload}`,
          method: 'DELETE',
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(
              accountingPeriodApi.util.invalidateTags(['Accounting Period']),
            );
          }
        },
        transformResponse: async (response: IAccountingPeriodResponse) => {
          return response?.data;
        },
        transformErrorResponse: async (baseQueryReturnValue) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      closeAccountingPeriod: builder.mutation({
        query: (accountingPeriodId: string) => ({
          url: `accounting-periods/${accountingPeriodId}/close`,
          method: 'POST',
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(
              accountingPeriodApi.util.invalidateTags(['Accounting Period']),
            );
          }
        },
        transformResponse: async (response: IAccountingPeriodResponse) => {
          return response?.data;
        },
        transformErrorResponse: async (baseQueryReturnValue) => {
          return handleError(baseQueryReturnValue);
        },
      }),
    }),
  });

export const {
  useGetAccountingPeriodQuery,
  useLazyGetAccountingPeriodQuery,
  useCreateAccountingPeriodMutation,
  useUpdateAccountingPeriodMutation,
  useDeleteAccountingPeriodMutation,
  useCloseAccountingPeriodMutation,
} = accountingPeriodApi;
